import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import produce from 'immer';
import { Issue, MonitorSeverity, IssueConditionValue, Comment, RobotCommunicationType }
    from '@moderepo/mode-apis';
import { modeUIContext, showSuccessNotification, showDialog, DialogType, DialogResponse, DialogResponseCode, setIsEditingForm, hideSlideOutPanel,
    SlideOutPanelLocation, showSlideOutPanel } from '@moderepo/mode-ui-state';
import { TFunction } from 'i18next';
import moment from 'moment';
import { Agent, ProjectAgent } from '@moderepo/mode-internal-apis';
import { fetchProjectAgents, fetchProjectDevices, selectProjectDevices, selectProjectAgent, selectProjectAgents,
    selectProjectById, ritzDataContext, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { SelectInputOption, FontIcon, capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_5, getRobotDefinitionByRobotClass, BaseCompAction, SlideOutComp }
    from '@moderepo/mode-app-components';
import { addCommentForIssue, createIssue, deleteCommentFromIssue, deleteIssue, fetchAllIssueComments, fetchIssue, fetchRobotById, fetchRobotCatalog,
    rcDataContext, selectAllIssueComments, selectIssueById, selectRobotById, selectRobotCatalog, updateCommentForIssue, updateIssue }
    from '@moderepo/mode-data-state-robot-cloud';
import { RouteHelper } from '../routes/RouteHelper';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { RCIssueInfo, RCIssueComments } from '../../components/robot-cloud';
import * as RCUtils from './RCUtils';
import { CommentGroup } from '../../components/robot-cloud/RCIssueComments';
import { RCRouteHelper } from './RCRouteHelper';
import { RCMonitorPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';
import { RCIssueInfoProps, RCIssueInfoUpdatableInputs } from '../../components/robot-cloud/RCIssueInfo';
import { RobotsTableHiddenFields } from './RCRobotsListingContainer';



const useStyle = makeStyles((theme: Theme) => {
    return {
        primaryAction: {
            '&.resolve-action': {
                background: theme.palette.success.main,
                color     : theme.palette.success.contrastText,
            },
            '&.unresolve-action': {
                background: theme.palette.error.main,
                color     : theme.palette.error.contrastText,
            },
        },

        secondaryAction: {
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});



/**
 * Custom hook to create an Array of Device Statuses used for CreateIssue alert action
 * @param trans
 */
const useCreateIssueSeverityOptions = (trans: TFunction): readonly SelectInputOption<MonitorSeverity>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MonitorSeverity>[]>([]);

    useEffect(() => {
        setOptions((Object.values(MonitorSeverity).filter((v: any) => {
            return typeof v === 'number';
        }) as MonitorSeverity[]).map((issueSeverity: MonitorSeverity): SelectInputOption<MonitorSeverity> => {
            return {
                label: capitalizeAll(trans(RCUtils.getMonitorSeverityName(issueSeverity))),
                value: issueSeverity,
            };
        }));
    }, [trans]);

    return options;
};



interface InputErrors {
    robot: string | null;
    severity: string | null;
    description: string | null;
}



interface RCIssuePreviewContainerProps {
    readonly projectId: number;
    readonly issueId: number | undefined;
    readonly preSelectedRobotId?: string;
    readonly preSelectedHomeId?: number;

    // these are OPTIONAL and only available if the user is on a User, Home, or Device page
    readonly context?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
        readonly deviceId?: number | undefined;
    }
}


/**
 * This page is used for showing preview of an issue.
 */
export const RCIssuePreviewContainer = (props: RCIssuePreviewContainerProps) => {

    const navigate = useNavigate();
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const selectedIssue = props.issueId ? selectIssueById(rcDataState, props.issueId) : undefined;
    const isNewIssue = props.issueId === undefined;
    const projectAgents = selectProjectAgents(dataState, projectId);
    const projectDevices = selectProjectDevices(dataState, projectId);
    const comments = selectedIssue ? selectAllIssueComments(rcDataState, selectedIssue.id) : undefined;

    // The device, home, and agents tied to the selected issue. We need the device info, home info, and agent info later on
    const issueRobot = selectRobotById(rcDataState, selectedIssue?.homeId, selectedIssue?.robotId);
    const issueCreatedAgent = selectedIssue && selectedIssue.createdAgentId
        ? selectProjectAgent(dataState, projectId, selectedIssue.createdAgentId) : undefined;
    const issueResolvedAgent = selectedIssue && selectedIssue.resolvedAgentId
        ? selectProjectAgent(dataState, projectId, selectedIssue.resolvedAgentId) : undefined;

    // Get the RobotInfo for the preSelectedRobotId
    const preselectedRobot = selectRobotById(rcDataState, props.preSelectedHomeId, props.preSelectedRobotId);

    // Get the robot definition for the robot that is tied to this issue
    const issueRobotDefinition = getRobotDefinitionByRobotClass(robotCatalog, issueRobot?.robotClass);


    const { trans } = useRitzTranslation(project);
    const routeHelper = RouteHelper.getInstance(navigate);
    const rcRouteHelper = RCRouteHelper.getInstance(navigate);
    const classes = useStyle();

    // Get the editing flag from UI State
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [inputErrors, setInputErrors] = useState<InputErrors>({
        robot: null, severity: null, description: null,
    });


    // The drop down options used by the Info component
    const issueSeverityOptions = useCreateIssueSeverityOptions(trans);


    /**
     * This container can be shown on any page so we need to get the current page's URL if it has deviceId.
     * We only need this URL to check if we are on a device details page later in the code.
     */
    const currentDevicePageUrl = useMemo(() => {
        if (props.context?.deviceId) {
            return routeHelper.getDevicePageUrl({
                projectId, deviceId: props.context.deviceId, userId: props.context?.userId, homeId: props.context?.homeId,
            });
        }
        return undefined;
    }, [routeHelper, projectId, props.context]);



    // Get the device details page URL for the device that is tied to the current selected issue
    const deviceDetailsPage = useMemo(() => {
        if (selectedIssue && selectedIssue.deviceId) {
            return routeHelper.getDevicePageUrl({
                projectId, deviceId: selectedIssue.deviceId, userId: props.context?.userId, homeId: props.context?.homeId,
            });
        }
        return undefined;
    }, [selectedIssue, routeHelper, projectId, props.context]);



    /**
     * Hide the slide out panel
     */
    const onHideSlideOutPanel = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);



    // Group the comments by agentId so that if 1 agent add multiple comments consecutively, we will display them as 1 group
    const commentGroups = useMemo((): CommentGroup[] => {
        const groups: CommentGroup[] = [];

        if (comments && projectAgents) {
            [...comments.items].sort((comment1: Comment, comment2: Comment): number => {
                // sort the comments by createdAt date with the latest comment last
                return moment(comment1.createdAt).valueOf() - moment(comment2.createdAt).valueOf();
            }).forEach((comment: Comment) => {

                const localizedCreatedDate = getDateTimeAndElapsedTime(comment.createdAt, trans, true);


                // If this is the first Comment or if the current Comment is from a DIFFERENT agent as the last comment, we will create a new group
                if (groups.length === 0 || groups[groups.length - 1].agentId !== comment.agentId) {

                    // Find the Agent object for the agent that created the comment
                    const createdAgent: ProjectAgent | undefined = comment.agentId ? projectAgents.find((agent: ProjectAgent) => {
                        return agent.agentId === comment.agentId;
                    }) : undefined;

                    // Get the name of the agent that created the comment. If agent not found (either agent is no longer exist or the
                    // comment is system generated), use the agent's ID as name or 'System' as name is comment is system generated
                    const agentName: string = createdAgent ? createdAgent.name : String(comment.agentId || trans('system.name'));

                    // Get the agent's initial. If the agent's name is less than 4 letters long then use the whole name. If not, use the first letter
                    // of each word in the name, up to 3 letters.
                    const agentInitial: string = (agentName.length <= 3 ? agentName : agentName.split(' ').map((name: string) => {
                        return name.charAt(0);
                    }).join('')).toUpperCase().substr(0, 3);


                    // Get the agent's avatar. For now, only System has avatar. When we allow agent to upload their photo as avatar, we will need
                    // to update this to use agent's avatar image.
                    const agentAvatar: JSX.Element | undefined = comment.sysGenerated
                        ? <FontIcon iconName={trans('system.avatar')} />
                        : undefined;


                    // Add a new Group to the groups list
                    groups.push({
                        id                     : comment.createdAt,
                        startDate              : localizedCreatedDate,
                        agentId                : comment.agentId,
                        agentName,
                        agentInitial,
                        agentAvatar,
                        isLoggedInAgentComments: Boolean(createdAgent && createdAgent.agentId === loggedInAgent.id),
                        comments               : [{
                            ...comment,
                            localizedCreatedDate,
                        }],
                    });
                } else {
                    // The current comment is from the same agent as the last comment so we just need to add the comment to the last group
                    groups[groups.length - 1].comments.push({
                        ...comment,
                        localizedCreatedDate,
                    });
                }
            });
        }

        return groups;
    }, [comments, projectAgents, loggedInAgent, trans]);



    // This useEffect is used for loading Issue data for the selected alert if we don't have the data yet
    useEffect(() => {
        if (props.issueId && !selectedIssue) {
            rcDataDispatch(fetchIssue(projectId, props.issueId)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.issueId, projectId, selectedIssue, rcDataDispatch]);


    /**
     * This use effect is used for loading createAgent data when an issue is selected. The issue data contains only createdAgentId but we also need
     * other information about the agent so we need to load agent details if we don't have the details yet
     */
    useEffect(() => {
        // If an issue is selected but we don't agent data, load agent data. However, there is no API for fetching 1 agent so we will fetch all
        // agents belonged to the project and then lookup agent data from that list
        if (selectedIssue && (selectedIssue.createdAgentId || selectedIssue.resolvedAgentId) && !projectAgents) {
            dataDispatch(fetchProjectAgents(projectId)).catch(() => {
                // ignore error because dataDispatch already took care of the error
            });
        }
    }, [dataDispatch, projectId, selectedIssue, projectAgents]);



    /**
     * This useEffect is used for loading all project's devices
     */
    useEffect(() => {
        if (!projectDevices) {
            dataDispatch(fetchProjectDevices(projectId)).catch(() => {
                // ignore error because dataDispatch already took care of the error
            });
        }
    }, [dataDispatch, projectId, projectDevices]);


    /**
     * Automatically set editing mode to true on component init if props.issueId is undefined
     */
    useEffect(() => {
        if (props.issueId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.issueId, onChangeEditingMode]);



    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, projectId, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot data for the issue's robot
     */
    useEffect(() => {
        if (!issueRobot && selectedIssue?.robotId) {
            rcDataDispatch(fetchRobotById(projectId, selectedIssue.homeId, selectedIssue.robotId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [projectId, issueRobot, selectedIssue, rcDataDispatch]);



    /**
     * This useEffect is used for loading robot data for the preselectedRobotId
     */
    useEffect(() => {
        if (!preselectedRobot && props.preSelectedHomeId && props.preSelectedRobotId) {
            rcDataDispatch(fetchRobotById(projectId, props.preSelectedHomeId, props.preSelectedRobotId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [props.preSelectedRobotId, props.preSelectedHomeId, projectId, preselectedRobot, rcDataDispatch]);


    /**
     * Function to fetch comments
     */
    const fetchComments = useCallback((issueId: number) => {
        rcDataDispatch(fetchAllIssueComments(projectId, issueId), false).catch(() => {
            // ignore error because dataDispatch already took care of the error.
        });
    }, [projectId, rcDataDispatch]);


    /**
     * This useEffect is used for fetching comments on component init
     */
    useEffect(() => {
        if (selectedIssue && !comments) {
            fetchComments(selectedIssue.id);
        }
    }, [selectedIssue, comments, fetchComments]);



    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            robot: null, severity: null, description: null,
        });
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onIssueInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    /**
     * Handler for when the user click Save input from the user form
     */
    const onIssueInfoFormSubmit = useCallback(async (inputs: RCIssueInfoUpdatableInputs): Promise<boolean> => {

        let result = false;

        if (projectDevices) {

            const newInputErrors: InputErrors = {
                robot: null, severity: null, description: null,
            };

            const newSeverity = inputs.severity;
            const newDescription = inputs.description;
            const newTicketLink = inputs.ticketLink;

            if (isNewIssue && !inputs.robot) {
                // if this is Create new issue flow then we need a robot
                newInputErrors.robot = 'error.input.robot.empty';
            }
            if (!newDescription) {
                newInputErrors.description = 'error.input.description.empty';
            }
            if (isNewIssue && !newSeverity) {
                newInputErrors.severity = 'error.input.issue_severity.empty';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);


            if (!newInputErrors.description && !newInputErrors.robot && !newInputErrors.severity) {
                // Inputs validated successfully

                if (isNewIssue) {
                    // Create new issue
                    if (inputs.robot && newSeverity && newDescription) {
                        try {
                            await rcDataDispatch(createIssue(projectId, loggedInAgent.id, {
                                homeId     : inputs.robot.homeId,
                                deviceId   : inputs.robot.deviceId || undefined,
                                robotId    : inputs.robot.id,
                                severity   : newSeverity,
                                description: newDescription,
                                ticketLink : newTicketLink,
                            }));

                            uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'label.issue' }));
                            closeEditing();
                            result = true;

                            // New issue created successfully close the preview panel
                            onHideSlideOutPanel();
                        } catch (error) {
                            /* ignore error */
                        }
                    }
                } else if (selectedIssue) {
                    // If update Issue
                    // Make sure the user changed something before we make an API call to update issue so that we don't wats an API call
                    if ((newDescription !== selectedIssue.description) || (newSeverity !== selectedIssue.severity)
                        || (newTicketLink !== selectedIssue.ticketLink)) {

                        try {
                            // dispatch an action to update the issue
                            await rcDataDispatch(updateIssue(projectId, loggedInAgent.id, selectedIssue.id, {
                                description: newDescription !== selectedIssue.description ? newDescription : undefined,
                                severity   : newSeverity !== selectedIssue.severity ? newSeverity : undefined,
                                ticketLink : newTicketLink !== selectedIssue.ticketLink ? newTicketLink : undefined,
                            }));

                            uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.issue' }));
                            closeEditing();
                            result = true;
                        } catch (error) {
                            /* ignore error */
                        }
                    } else {
                        // nothing changed
                        result = true;
                        closeEditing();
                    }
                } else {
                    // newDescription and newResolvedAgentId is the same so no need to make api call
                    closeEditing();
                }
            }
        }

        return Promise.resolve(result);

    }, [projectDevices, isNewIssue, selectedIssue, rcDataDispatch, projectId, loggedInAgent.id, uiDispatch, closeEditing, onHideSlideOutPanel]);



    /**
     * On Investigate Issue action clicked
     */
    const onInvestigateActionClicked = useCallback((issue: Issue) => {
        rcRouteHelper.gotoInvestigateIssuePage({
            projectId,
            issueId : issue.id,
            deviceId: props.context?.deviceId,
            userId  : props.context?.userId,
            homeId  : props.context?.homeId,
        });
        uiDispatch(hideSlideOutPanel());
    }, [rcRouteHelper, projectId, props.context, uiDispatch]);


    /**
     * On View Device Details action clicked
     */
    const onViewDeviceDetailsActionClicked = useCallback((issue: Issue) => {
        // NOTE: use the deviceId that is tied to the selected issue instead of the deviceId of the device page we are on incase they are different.
        // As for homeId, the Issue object has a homeId but it might not be valid anymore since a device can be moved to another home. So, we will
        // use the homeId that is tied to the current page the user is on, not the issue's homeId.
        if (issue.deviceId) {
            routeHelper.gotoDevicePage({
                projectId, deviceId: issue.deviceId, userId: props.context?.userId, homeId: props.context?.homeId,
            });
            uiDispatch(hideSlideOutPanel());
        }
    }, [routeHelper, projectId, props.context, uiDispatch]);


    /**
     * On View Robot Details action clicked
     */
    const onViewRobotDetailsActionClicked = useCallback((issue: Issue) => {
        // NOTE: use the deviceId that is tied to the selected issue instead of the deviceId of the device page we are on incase they are different.
        if (issue.deviceId) {
            rcRouteHelper.gotoRobotPage({
                projectId, userId: props.context?.userId, homeId: issue.homeId, deviceId: issue.deviceId, robotId: issue.robotId,
            });
            uiDispatch(hideSlideOutPanel());
        }
    }, [rcRouteHelper, projectId, props.context, uiDispatch]);


    /**
     * On View Monitor Details action clicked
     */
    const onViewMonitorDetailsActionClicked = useCallback((issue: Issue) => {
        const options: RCMonitorPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    monitorId: issue.alertConditionId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [projectId, uiDispatch]);


    /**
     * On Delete Issue action clicked
     */
    const onRemoveIssueActionClicked = useCallback((issue: Issue) => {
        // Confirm before deleting Monitor
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_remove_issue.title',
            message       : 'dialog.confirm_remove_issue.message',
            messageData   : { issue_id: issue.id },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    rcDataDispatch(deleteIssue(projectId, issue.id)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', { term: 'label.issue' }));

                        // Issue deleted, clear the selected entity
                        onHideSlideOutPanel();
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [uiDispatch, rcDataDispatch, projectId, onHideSlideOutPanel]);



    /**
     * Make an API call toggle the current selected issue. If the selected issue is NOT resolve, then mark it as resolved. Otherwise, mark it as NOT
     * resolved.
     */
    const toggleIssueResolveStatus = useCallback(async () => {
        if (selectedIssue) {
            try {
                const issueResolved = Boolean(selectedIssue.resolvedAt);
                await rcDataDispatch(updateIssue(projectId, loggedInAgent.id, selectedIssue.id, {
                    unresolved     : !!issueResolved,
                    resolvedAgentId: issueResolved ? undefined : loggedInAgent.id,
                }));
                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.issue' }));
            } catch (error) {
                // do nothing because dataDispatch already handle the error
            }
        }
    }, [selectedIssue, rcDataDispatch, projectId, loggedInAgent.id, uiDispatch]);



    /**
     * On Add Comment form submit
     */
    const onAddCommentSubmit = useCallback(async (message: string): Promise<boolean> => {
        if (message.length > 0 && selectedIssue) {
            try {
                await rcDataDispatch(addCommentForIssue(projectId, selectedIssue.id, loggedInAgent.id, message));
                return Promise.resolve(true);
            } catch (error) {
                // do nothing because the dataDispatch already handled the error
            }
        }

        return Promise.resolve(false);
    }, [selectedIssue, rcDataDispatch, projectId, loggedInAgent.id]);


    /**
     * On Edit Comment form submit
     */
    const onEditCommentSubmit = useCallback(async (comment: Comment, newMessage: string): Promise<boolean> => {
        if (newMessage.length > 0 && selectedIssue) {
            if (newMessage !== comment.message) {
                try {
                    await rcDataDispatch(updateCommentForIssue(projectId, selectedIssue.id, comment.id, newMessage));
                    return Promise.resolve(true);
                } catch (error) {
                    // do nothing because the dataDispatch already handled the error
                }
            }

            // Message has not changed so just resolve true without making API call
            return Promise.resolve(true);
        }

        return Promise.resolve(false);
    }, [selectedIssue, rcDataDispatch, projectId]);



    /**
     * On Delete comment clicked. Confirm with the agent before making API call to delete the comment.
     */
    const onDeleteComment = useCallback(async (comment: Comment) => {
        if (comment && selectedIssue) {
            uiDispatch(showDialog({
                type          : DialogType.CONFIRM,
                title         : 'dialog.confirm_delete_issue_comment.title',
                message       : 'dialog.confirm_delete_issue_comment.message',
                positiveButton: 'label.delete',
                negativeButton: 'label.cancel',
                onClose       : async (response?: DialogResponse): Promise<void> => {
                    if (response && response.code === DialogResponseCode.POSITIVE) {
                        try {
                            await rcDataDispatch(deleteCommentFromIssue(projectId, selectedIssue.id, comment.id));
                        } catch (error) {
                            // do nothing because the dataDispatch already handled the error
                        }
                    }
                },
            }));
        }
    }, [selectedIssue, uiDispatch, rcDataDispatch, projectId]);



    /**
     * On reload comments button clicked.
     */
    const onReloadCommentsClick = useCallback(() => {
        if (selectedIssue) {
            fetchComments(selectedIssue.id);
        }
    }, [selectedIssue, fetchComments]);



    /**
     * onCancelEdit click, we will stop editing mode. And if we are in create new Issue mode, we will have to also unselect the current
     * selected issue
     */
    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (isNewIssue) {
            onHideSlideOutPanel();
        }
    }, [closeEditing, isNewIssue, onHideSlideOutPanel]);



    // Create the labels to be used by the IssueCommentsComponent and cache it
    const issueCommentsComponentLabels = useMemo(() => {
        return {
            you                      : capitalizeAll(trans('label.you')),
            addCommentInputLabel     : capitalizeAll(trans('input.add_comment.label')),
            addCommentPlaceholder    : capitalizeFirst(trans('input.add_comment.placeholder')),
            addCommentHintText       : capitalizeFirst(trans('input.add_comment.hint_text')),
            emptyCommentsListMessage : capitalizeFirst(trans('label.empty_issue_comments_list')),
            editCommentInputLabel    : capitalizeAll(trans('label.edit_comment')),
            deleteCommentButtonLabel : capitalizeAll(trans('label.delete_comment')),
            reloadCommentsButtonLabel: capitalizeAll(trans('label.reload_comments')),
            updateButtonLabel        : capitalizeFirst(trans('label.update')),
            cancelButtonLabel        : capitalizeFirst(trans('label.cancel')),
        };
    }, [trans]);



    // This Preview Panel can also be shown on the Investigate Issue page. If we are already on the investigate issue page, we don't
    // want to show the "Investigate Issue" button so we need to check if we are on the investigate issue page.
    const investigateIssuePageUrl = selectedIssue ? rcRouteHelper.getInvestigateIssuePageUrl({
        projectId,
        issueId : selectedIssue.id,
        deviceId: props.context?.deviceId,
        userId  : props.context?.userId,
        homeId  : props.context?.homeId,
    }) : undefined;
    const isOnInvestigationPage = investigateIssuePageUrl && window.location.href.includes(investigateIssuePageUrl);


    const robotsTableHiddenFields = useMemo((): RobotsTableHiddenFields => {
        return {
            preview                : true,
            availability           : true,
            createdAt              : true,
            updatedAt              : true,
            overrideRobotDefinition: true,
        };
    }, []);


    const issueInfoInputSettings = useMemo((): RCIssueInfoProps['fields'] => {
        return {
            id: {
                label       : capitalizeAll(trans('label.id')),
                displayValue: selectedIssue?.id.toString() || '',
                hidden      : isNewIssue,
            },
            deviceId: {
                label: capitalizeAll(trans(issueRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY
                    ? 'label.gateway_id' : 'label.device_id')),
                displayValue: selectedIssue?.deviceId?.toString() || '',
                hidden      : isNewIssue,
            },
            robotId: {
                label       : capitalizeAll(trans('label.robot_id')),
                displayValue: selectedIssue?.robotId || '',
                hidden      : isNewIssue,
            },
            homeId: {
                label       : capitalizeAll(trans('label.home_id')),
                displayValue: selectedIssue?.homeId.toString() || '',
                hidden      : isNewIssue,
            },
            robot: {
                label                      : capitalizeAll(trans('label.robot_id')),
                value                      : preselectedRobot,
                robotsListingContainerProps: {
                    projectId   : props.projectId,
                    title       : capitalizeFirst(trans('panel.select_robot.title')),
                    itemsPerPage: ITEMS_PER_PAGE_5,
                    hiddenFields: robotsTableHiddenFields,
                },
                error   : inputErrors.robot ? trans(inputErrors.robot) : '',
                editable: isNewIssue && !props.preSelectedRobotId && !props.preSelectedHomeId,
                hidden  : !isNewIssue,
            },
            description: {
                label       : capitalizeAll(trans('label.description')),
                displayValue: selectedIssue ? selectedIssue.description : '',
                value       : selectedIssue ? selectedIssue.description : '',
                error       : inputErrors.description ? trans(inputErrors.description) : '',
                editable    : true,
            },
            severity: {
                label       : capitalizeAll(trans('label.issue_severity')),
                displayValue: selectedIssue ? capitalizeAll(trans(
                    RCUtils.getMonitorSeverityName(selectedIssue.severity),
                )) : '',
                value   : selectedIssue ? selectedIssue.severity : undefined,
                options : issueSeverityOptions,
                error   : inputErrors.severity ? trans(inputErrors.severity) : '',
                editable: true,
            },
            resolveStatus: {
                label       : capitalizeAll(trans('label.status')),
                displayValue: selectedIssue && selectedIssue.resolvedAt
                    ? capitalizeFirst(trans('label.issue_resolve_status', {
                        agent_name: String(
                            issueResolvedAgent?.name
                            || selectedIssue.resolvedAgentId
                            || capitalizeAll(trans('system.name')),
                        ),
                        elapsed_time: getDateTimeAndElapsedTime(selectedIssue.resolvedAt, trans, false),
                    }))
                    : capitalizeAll(trans('label.not_resolved')),
                hidden  : isNewIssue,
                editable: false,
            },
            resolvedBy: {
                label       : capitalizeAll(trans('label.resolved_by')),
                displayValue: selectedIssue && selectedIssue.resolvedAt
                    ? String(
                        issueResolvedAgent?.name
                        || selectedIssue.resolvedAgentId
                        || capitalizeAll(trans('system.name')),
                    )   // Try using agent's name, if not try use resolveAgentId, else assume issue resolved by System
                    : capitalizeAll(trans('label.not_resolved')),
                hidden: true,
            },
            resolvedTime: {
                label       : capitalizeAll(trans('label.date_resolved')),
                displayValue: selectedIssue && selectedIssue.resolvedAt
                    ? getDateTimeAndElapsedTime(selectedIssue.resolvedAt, trans)
                    : capitalizeAll(trans('label.not_resolved')),
                hidden: true,
            },
            ticketLink: {
                label   : capitalizeAll(trans('label.external_ticket_link')),
                value   : selectedIssue ? selectedIssue.ticketLink : '',
                editable: true,
            },
            createdBy: {
                label       : capitalizeAll(trans('label.agent')),
                displayValue: selectedIssue && selectedIssue.createdAgentId
                    ? (issueCreatedAgent?.name || selectedIssue.createdAgentId).toString()
                    : capitalizeAll(trans('label.none')),
                hidden: isNewIssue || !selectedIssue?.createdAgentId,
            },
            creationTime: {
                label       : capitalizeAll(trans('label.date_created')),
                displayValue: selectedIssue ? getDateTimeAndElapsedTime(selectedIssue.createdAt, trans) : '',
                hidden      : isNewIssue,
            },
            monitorId: {
                label       : capitalizeAll(trans('label.monitor_id')),
                displayValue: selectedIssue && selectedIssue.alertConditionId
                    ? selectedIssue.alertConditionId.toString()
                    : capitalizeAll(trans('label.none')),
                clickable: true,
                hidden   : Boolean(isNewIssue || selectedIssue?.createdAgentId),
            },
            values: selectedIssue && selectedIssue.conditionValues
                ? selectedIssue.conditionValues.map((value: IssueConditionValue, index: number) => {
                    return {
                        label       : `${capitalizeAll(trans('label.issue_triggered_value'))} - ${index + 1}`,
                        displayValue: RCUtils.getIssueConditionAsString(
                            robotCatalog, value, issueRobot?.robotClass, trans,
                        ),
                        multiline: true,
                        hidden   : Boolean(isNewIssue || selectedIssue.createdAgentId),
                    };
                }) : [],
        };
    }, [selectedIssue, inputErrors, isNewIssue, issueCreatedAgent, issueResolvedAgent, issueRobot, issueSeverityOptions, props.projectId,
        robotCatalog, robotsTableHiddenFields, trans, props.preSelectedHomeId, props.preSelectedRobotId, preselectedRobot, issueRobotDefinition]);



    /**
     * Create a list of primary actions to be displayed in the SlideOutPanel action bar
     */
    const primaryActions = useMemo(() => {
        if (!isNewIssue && selectedIssue) {
            const actions: BaseCompAction[] = [];

            /*
                Only show Investigate Issue button if we are not on the Investigate Issue page
            */
            if (!isOnInvestigationPage) {
                actions.push({
                    className: clsx(classes.primaryAction),
                    icon     : 'search',
                    label    : capitalizeAll(trans('label.investigate_issue')),
                    onClick  : () => {
                        onInvestigateActionClicked(selectedIssue);
                    },
                });
            }

            actions.push({
                className: clsx(classes.primaryAction, selectedIssue.resolvedAt ? 'unresolve-action' : 'resolve-action'),
                label    : selectedIssue.resolvedAt ? capitalizeAll(trans('label.unresolve_issue')) : capitalizeAll(trans('label.resolve_issue')),
                onClick  : toggleIssueResolveStatus,
            });

            return actions;
        }

        return undefined;
    }, [classes.primaryAction, isNewIssue, isOnInvestigationPage, onInvestigateActionClicked, selectedIssue, toggleIssueResolveStatus, trans]);


    /**
     * Create a list of secondary actions to be displayed in the SlideOutPanel dropdown menu
     */
    const secondaryActions = useMemo(() => {
        if (!isNewIssue && selectedIssue) {
            const actions: BaseCompAction[] = [];

            /*
                Only show Menu option to go to Device Details page if we are NOT already on the device details page
                for the device that is tied to this issue
            */
            if (!currentDevicePageUrl || (deviceDetailsPage && currentDevicePageUrl && !currentDevicePageUrl.includes(deviceDetailsPage))) {
                actions.push({
                    className: clsx(classes.secondaryAction),
                    disabled : !selectedIssue.deviceId,
                    icon     : trans('icons.device'),
                    label    : capitalizeAll(trans(issueRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY
                        ? 'label.view_gateway_details' : 'label.view_device_details')),
                    onClick: () => {
                        onViewDeviceDetailsActionClicked(selectedIssue);
                    },
                });
            }

            actions.push({
                className: clsx(classes.secondaryAction),
                icon     : 'mode-icon robot-solid',
                label    : capitalizeAll(trans('label.view_robot_details')),
                onClick  : () => {
                    onViewRobotDetailsActionClicked(selectedIssue);
                },
            });

            actions.push({
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.delete_issue')),
                onClick  : () => {
                    onRemoveIssueActionClicked(selectedIssue);
                },
            });

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, currentDevicePageUrl, deviceDetailsPage, isNewIssue, issueRobotDefinition, onRemoveIssueActionClicked,
        onViewDeviceDetailsActionClicked, onViewRobotDetailsActionClicked, selectedIssue, trans]);



    const slideOutCompContent = useMemo(() => {
        if (isNewIssue || selectedIssue) {
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <RCIssueInfo
                            className={clsx('transparent', isEditing && 'editing')}
                            formProps={{
                                className: 'single-column',
                            }}
                            isEditing={isEditing}
                            title={capitalizeAll(trans(selectedIssue ? 'label.issue_details' : 'label.create_issue'))}
                            icon="settings"
                            issue={selectedIssue}
                            fields={issueInfoInputSettings}
                            texts={{
                                createdByGroupTitle: capitalizeAll(trans('label.created_by')),
                            }}
                            actions={{
                                startEdit: {
                                    onClick: () => {
                                        onChangeEditingMode(true);
                                    },
                                },
                                cancelEdit: {
                                    label  : capitalizeAll(trans('label.cancel')),
                                    onClick: onCancelEdit,
                                },
                                saveUpdate: {
                                    label  : capitalizeAll(trans('label.save')),
                                    onClick: onIssueInfoFormSubmit,
                                },
                            }}
                            onFieldClicked={(name: string) => {
                                if (selectedIssue) {
                                    if (name === 'deviceId') {
                                        onViewDeviceDetailsActionClicked(selectedIssue);
                                    } else if (name === 'alertId') {
                                        onViewMonitorDetailsActionClicked(selectedIssue);
                                    } else {
                                        onChangeEditingMode(true);
                                    }
                                }
                            }}
                            onFormInputChange={onIssueInfoFormInputChange}
                        />
                    </Grid>

                    {selectedIssue && (
                        <Grid item xs={12}>
                            <RCIssueComments
                                className={clsx('transparent')}
                                title={capitalizeAll(trans('label.issue_comment', { count: 2 }))}
                                icon="message"
                                issue={selectedIssue}
                                commentGroups={commentGroups}
                                labels={issueCommentsComponentLabels}
                                onAddCommentSubmit={onAddCommentSubmit}
                                onEditCommentSubmit={onEditCommentSubmit}
                                onDeleteComment={onDeleteComment}
                                onReloadCommentsClick={onReloadCommentsClick}
                            />
                        </Grid>
                    )}
                </Grid>
            );
        }

        return (
            <CircularProgress />
        );
    }, [commentGroups, isEditing, isNewIssue, issueCommentsComponentLabels, issueInfoInputSettings, onAddCommentSubmit, onCancelEdit,
        onChangeEditingMode, onDeleteComment, onEditCommentSubmit, onIssueInfoFormInputChange, onIssueInfoFormSubmit, onReloadCommentsClick,
        onViewMonitorDetailsActionClicked, onViewDeviceDetailsActionClicked, selectedIssue, trans]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            closable={!isNewIssue}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
